import { useEffect, useState } from 'react'

import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import _ from 'lodash'
import styled from 'styled-components'
import { IFsNodeSchema } from '@cloudike/web_files'
import {
  RotateRightOutlined,
  DeleteOutlined,
  MoreOutlined,
  CheckOutlined,
  Table, Dropdown, Menu, Typography,
  Image,
  Spin
} from 'shared/ui'
import { ROUTES } from 'shared/constants/routesPaths'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'

import { RecentlyDeletedToolbarAction } from '../model/slice/recentlyDeletedSlice'

import { EmptyState } from './EmptyState'
interface Props {
  items: IFsNodeSchema[]
  onRecover: (id: string, name: string) => void
  onDelete: (id: string, name: string) => void
  selectedRowIds: Set<string>
  onSelectionChange: (selectedIds: Set<string>) => void
  listRef: React.RefObject<HTMLDivElement>
  onScrollEnd: () => void
  disableLoadMore: boolean
}

export const RecentlyDeletedList = ({
  items,
  onRecover,
  onDelete,
  selectedRowIds,
  onSelectionChange,
  listRef,
  onScrollEnd,
  disableLoadMore,
}: Props) => {
  const { t } = useTranslation()
  const [typeFilters, setTypeFilters] = useState<string[]>(['board', 'asset'])
  const [lastSelectedRowId, setLastSelectedRowId] = useState<string | null>(null)
  const navigate = useNavigate()

  const { ref: loaderRef, inView } = useInView()

  useEffect(() => {
    if (items.length && inView && !disableLoadMore) {
      onScrollEnd()
    }
  }, [inView, items, disableLoadMore])

  const mapTypeToGroup = (type: string): 'board' | 'asset' =>
    type === 'dir' ? 'board' : 'asset'

  const getSmallPreviewLink = (item: IFsNodeSchema) => {
    if (!item?._embedded?.previews?.sizes?.small) return ''
    const urlTemplate = _.template(item._embedded.previews.url_template, {
      interpolate: /\{(.+?)\}/g,
    })
    return urlTemplate({ size_name: 'small' }) + `&jwt=${item._embedded.previews.jwt}`
  }

  const handleRowClick = (e: React.MouseEvent, clickedId: string) => {
    const isShift = e.shiftKey
    const isMeta = e.metaKey || e.ctrlKey
    const ids = filteredItems.map((item) => item.id)
    const clickedIndex = ids.indexOf(clickedId)

    const next = new Set(selectedRowIds)

    if (isShift && lastSelectedRowId && selectedRowIds.has(lastSelectedRowId)) {
      const lastIndex = ids.indexOf(lastSelectedRowId)
      if (lastIndex !== -1 && clickedIndex !== -1) {
        const [start, end] = [lastIndex, clickedIndex].sort((a, b) => a - b)
        ids.slice(start, end + 1).forEach(id => next.add(id))
      }
    } else if (isMeta) {
      next.has(clickedId) ? next.delete(clickedId) : next.add(clickedId)
      setLastSelectedRowId(clickedId)
    } else {
      if (next.size === 1 && next.has(clickedId)) {
        next.clear()
      } else {
        next.clear()
        next.add(clickedId)
      }
      setLastSelectedRowId(clickedId)
    }

    onSelectionChange(next)
  }

  const getActionMenu = (node: IFsNodeSchema) => (
    <Menu
      onClick={(e) => {
        e.domEvent.stopPropagation()
        if (e.key === RecentlyDeletedToolbarAction.Recover) onRecover(node.id, node.name)
        if (e.key === RecentlyDeletedToolbarAction.Delete) onDelete(node.id, node.name)
      }}
      items={[
        {
          key: RecentlyDeletedToolbarAction.Recover,
          label: t('a_common_recover'),
          icon: <RotateRightOutlined />,
        },
        {
          key: RecentlyDeletedToolbarAction.Delete,
          label: t('a_common_deleteForever'),
          icon: <DeleteOutlined />,
          danger: true,
        },
      ]}
    />
  )

  const columns: ColumnsType<IFsNodeSchema> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      render: (_, item) => (
        <SRowPreviewWrapper>
          <Image
            src={getSmallPreviewLink(item) || '/images/boardPreview.svg'}
            alt=""
            width={88}
            height={48}
            style={{ borderRadius: 4, objectFit: 'cover' }}
            preview={false}
          />

          <SEllipsisText ellipsis={{ tooltip: item.name }}>
            {item.name}
          </SEllipsisText>
        </SRowPreviewWrapper>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      filterDropdown: ({ confirm }) => (
        <SFilterDropdownContainer>
          {['board', 'asset'].map((group) => {
            const isSelected = typeFilters.includes(group)
            return (
              <SFilterOption
                key={group}
                selected={isSelected}
                onClick={() => {
                  const next = isSelected
                    ? typeFilters.filter((t) => t !== group)
                    : [...typeFilters, group]
                  setTypeFilters(next)
                  confirm()
                }}
              >
                <span>
                  {group === 'board' ? 'Boards' : 'Assets'}
                </span>

                {isSelected && <CheckOutlined style={{ color: '#9254de', fontSize: 14 }} />}
              </SFilterOption>
            )
          })}
        </SFilterDropdownContainer>
      ),
      filteredValue: typeFilters,
      onFilter: () => true,
      render: (type: string) => (type === 'dir' ? 'Board' : 'Asset'),
    },
    {
      title: 'Deleted On',
      dataIndex: 'updated',
      key: 'updated',
      render: (_, node) => dayjs(node.updated).format('MMM D, YYYY'),
      sorter: (a, b) => dayjs(a.updated).unix() - dayjs(b.updated).unix(),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Original Board',
      key: 'original_board',
      render: (_, node) =>
        node.parent_name && node.parent_id ? (
          <SEllipsisText>
            <SParentBoardLink
              onClick={(e) => {
                e.stopPropagation()
                navigate(`${ROUTES.dashboard.board}/${node.parent_id}`)
              }}
              ellipsis={{ tooltip: node.parent_name }}
            >
              {node.parent_name}
            </SParentBoardLink>
          </SEllipsisText>

        ) : (
          <SEmptyParent>
-
          </SEmptyParent>
        ),
    },
    {
      title: 'Deleted by',
      dataIndex: 'last_editor_name',
      key: 'last_editor_name',
      render: (name) => (
        <span>
          {name}
        </span>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, node) => (
        <Dropdown overlay={getActionMenu(node)}
          trigger={['click']}
          placement="bottomRight"
        >
          <SActionIcon onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ]

  const filteredItems = items
    .filter((item) => typeFilters.includes(mapTypeToGroup(item.type)))
    .sort((a, b) => dayjs(b.updated).unix() - dayjs(a.updated).unix())

  if (items.length === 0) {
    return <EmptyState />
  }

  return (
    <STableWrapper ref={listRef}>
      <Table
        rowKey="id"
        dataSource={filteredItems}
        columns={columns}
        pagination={false}
        showHeader
        locale={{ emptyText: '' }}
        onRow={(node) => ({
          onClick: (e) => handleRowClick(e, node.id)
        })}
        rowClassName={(node) =>
          selectedRowIds.has(node.id) ? 'ant-table-row-selected' : ''
        }
      />

      {!disableLoadMore && (
        <SLoaderRow ref={loaderRef}>
          <SSpinner />
        </SLoaderRow>
      )}
    </STableWrapper>
  )
}

const STableWrapper = styled.div`
  .ant-table-tbody > tr {
    cursor: pointer;
  }
  .ant-table-tbody > tr > td,
  .ant-table-thead > tr > th {
    border-bottom: none !important;
  }
`

const SRowPreviewWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SEllipsisText = styled(Typography.Text)`
  max-width: 250px !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
`

const SActionIcon = styled(MoreOutlined)`
  font-size: 18px;
  color: rgba(0, 0, 0, 0.65);
  cursor: pointer;
`

const SFilterDropdownContainer = styled.div`
  padding: 8px;
  width: 140px;
`

const SFilterOption = styled.div<{ selected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;

  &:hover {
    background-color: #f5f5f5;
  }
  span {
    font-size: 14px;
    &.anticon.anticon-check {
      color: var(--color-primary) !important;
      
    }
  }
`

const SParentBoardLink = styled(Typography.Text)`
  &:hover {
    color: var(--color-primary);
  }
`

const SEmptyParent = styled(Typography.Text).attrs({ type: 'secondary' })``

const SLoaderRow = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const SSpinner = styled(Spin)``
